import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Download, Printer, Image as ImageIcon, Loader2, Check } from 'lucide-react';
import { API, auth } from '../../organization/finance/shared';
import { toast } from '../../../lib/toast';
import CvDocument, { CV_TEMPLATES, ACCENTS, A4 } from './CvTemplates';
import { downloadCvPdf, downloadCvPng, printCv } from './cvExport';

const STORE_KEY = 'tasyar_cv_prefs';

export default function CvStudioModal({ open, onClose, profile }) {
  const pageRef = useRef(null);
  const [data, setData] = useState({ experiences: [], certifications: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const saved = (() => { try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch { return {}; } })();
  const [template, setTemplate] = useState(saved.template || 'executive');
  const [accent, setAccent] = useState(saved.accent || 'teal');
  const [showContact, setShowContact] = useState(saved.showContact !== false);
  const [showBrand, setShowBrand] = useState(saved.showBrand !== false);

  useEffect(() => {
    if (!open) return;
    localStorage.setItem(STORE_KEY, JSON.stringify({ template, accent, showContact, showBrand }));
  }, [open, template, accent, showContact, showBrand]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/coaches/profile/me/experiences`, auth()).catch(() => ({ data: [] })),
      axios.get(`${API}/coaches/profile/me/certifications`, auth()).catch(() => ({ data: [] })),
    ]).then(([e, c]) => {
      if (!alive) return;
      setData({ experiences: e.data || [], certifications: c.data || [] });
      setLoading(false);
    });
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);

  if (!open) return null;

  const filename = `السيرة_الذاتية_${profile?.full_name_ar || 'مدرب'}`;
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'pdf') await downloadCvPdf(pageRef.current, filename);
      if (kind === 'png') await downloadCvPng(pageRef.current, filename);
      if (kind === 'print') await printCv(pageRef.current);
      if (kind !== 'print') toast.success('تم التصدير بنجاح');
    } catch {
      toast.error('تعذّر التصدير، حاول مرة أخرى');
    }
    setBusy('');
  };

  const ActionBtn = ({ kind, icon: Icon, label, primary, testId }) => (
    <button
      onClick={() => run(kind)}
      disabled={Boolean(busy) || loading}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-95 disabled:opacity-50 ${primary ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/25 hover:brightness-105' : 'border border-slate-200 bg-white text-slate-700 hover:border-teal-300'}`}
    >
      {busy === kind ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />} {label}
    </button>
  );

  return ReactDOM.createPortal(
    <div className="fixed left-0 top-0 z-[99999] flex h-screen w-screen flex-col bg-slate-900/70 backdrop-blur-sm" data-testid="cv-studio-modal">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-6">
        <div>
          <h2 className="text-base font-bold text-white">استوديو السيرة الذاتية</h2>
          <p className="text-xs text-white/60">ثلاثة قوالب احترافية — صفحة واحدة بدقة طباعة عالية</p>
        </div>
        <button onClick={onClose} className="rounded-xl border border-white/15 bg-white/10 p-2 text-white transition-colors hover:bg-white/20" data-testid="cv-studio-close-btn"><X size={18} /></button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col-reverse lg:flex-row">
        {/* لوحة التحكم */}
        <div className="w-full shrink-0 overflow-y-auto border-t border-white/10 bg-white p-4 lg:w-80 lg:border-s lg:border-t-0 lg:p-5">
          <h3 className="mb-2.5 text-xs font-extrabold text-slate-500">اختر القالب</h3>
          <div className="space-y-2">
            {CV_TEMPLATES.map((t) => (
              <button
                key={t.key}
                onClick={() => setTemplate(t.key)}
                data-testid={`cv-template-${t.key}`}
                className={`flex w-full items-start gap-3 rounded-xl border p-3 text-right transition-all active:scale-[0.98] ${template === t.key ? 'border-teal-400 bg-teal-50/70 shadow-[0_2px_10px_rgba(13,148,136,0.12)]' : 'border-slate-200 bg-white hover:border-teal-200'}`}
              >
                <TemplateThumb kind={t.key} active={template === t.key} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
                    {t.name}{template === t.key && <Check size={13} className="text-teal-600" />}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500">{t.desc}</span>
                </span>
              </button>
            ))}
          </div>

          <h3 className="mb-2.5 mt-5 text-xs font-extrabold text-slate-500">اللون الأساسي</h3>
          <div className="flex flex-wrap gap-2">
            {ACCENTS.map((a) => (
              <button
                key={a.key}
                onClick={() => setAccent(a.key)}
                title={a.name}
                data-testid={`cv-accent-${a.key}`}
                className={`h-9 w-9 rounded-xl transition-all active:scale-90 ${accent === a.key ? 'ring-2 ring-offset-2 ring-slate-800' : 'ring-1 ring-slate-200'}`}
                style={{ background: `linear-gradient(135deg, ${a.c}, ${a.d})` }}
              />
            ))}
          </div>

          <label className="mt-5 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-semibold text-slate-700" data-testid="cv-toggle-contact">
            إظهار الجوال والبريد
            <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={showContact} onChange={(e) => setShowContact(e.target.checked)} />
          </label>
          <label className="mt-2 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-semibold text-slate-700" data-testid="cv-toggle-brand">
            توقيع تسيار أسفل الصفحة
            <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={showBrand} onChange={(e) => setShowBrand(e.target.checked)} />
          </label>

          <div className="mt-5 grid grid-cols-1 gap-2">
            <ActionBtn kind="pdf" icon={Download} label="تحميل PDF" primary testId="cv-download-pdf-btn" />
            <div className="grid grid-cols-2 gap-2">
              <ActionBtn kind="png" icon={ImageIcon} label="صورة PNG" testId="cv-download-png-btn" />
              <ActionBtn kind="print" icon={Printer} label="طباعة" testId="cv-print-btn" />
            </div>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
            يُصغَّر المحتوى تلقائياً ليبقى في صفحة A4 واحدة. لمحتوى أطول، أخفِ بعض العناصر أو اختصر الأوصاف للحصول على أفضل مظهر.
          </p>
        </div>

        {/* المعاينة */}
        <div className="min-h-0 flex-1 overflow-auto p-4 sm:p-8" data-testid="cv-preview-area">
          <div className="mx-auto" style={{ width: A4.w, maxWidth: '100%' }}>
            <div
              className="origin-top shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
              style={{ width: A4.w, height: A4.h, transform: 'scale(var(--cv-zoom, 1))' }}
            >
              {loading ? (
                <div className="flex h-full w-full items-center justify-center bg-white text-sm text-slate-400">جارِ تحميل بيانات ملفك...</div>
              ) : (
                <CvDocument
                  ref={pageRef}
                  template={template}
                  accent={accent}
                  profile={profile}
                  experiences={data.experiences}
                  certifications={data.certifications}
                  options={{ contact: showContact, brand: showBrand }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const TemplateThumb = ({ kind, active }) => {
  const line = (w) => <span className="block h-[3px] rounded-full bg-slate-200" style={{ width: w }} />;
  return (
    <span className={`flex h-16 w-12 shrink-0 flex-col overflow-hidden rounded-lg border bg-white ${active ? 'border-teal-300' : 'border-slate-200'}`}>
      {kind === 'executive' && (
        <>
          <span className="block h-5 w-full bg-slate-700" />
          <span className="flex flex-col gap-1 p-1.5">{line('100%')}{line('80%')}{line('90%')}{line('60%')}</span>
        </>
      )}
      {kind === 'sidebar' && (
        <span className="flex h-full w-full">
          <span className="flex flex-1 flex-col gap-1 p-1.5">{line('100%')}{line('80%')}{line('95%')}{line('70%')}</span>
          <span className="block h-full w-4 bg-slate-700" />
        </span>
      )}
      {kind === 'minimal' && (
        <span className="flex flex-col gap-1 p-1.5 pt-2.5">
          <span className="block h-[5px] w-3/4 self-end rounded-full bg-slate-700" />
          <span className="my-0.5 block h-px w-full bg-slate-300" />
          {line('100%')}{line('85%')}{line('92%')}
        </span>
      )}
    </span>
  );
};
